import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import TickerSearchInput from '../components/common/TickerSearchInput';
import { motion } from 'framer-motion';
import { 
  BuildingOfficeIcon, 
  ChartBarIcon, 
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

interface EligibilityResult {
  eligible: boolean;
  ticker: string;
  companyName: string;
  marketCap: number;
  averageVolume30Day: number;
  lastClose: number;
  exchange: string;
  sharesOutstanding?: number;
  message?: string;
  reasons?: string[];
  data?: {
    eligibilityId: string;
    [key: string]: any;
  };
}

const AuthenticatedNewFacility: React.FC = () => {
  const navigate = useNavigate();
  const [ticker, setTicker] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<EligibilityResult | null>(null);
  const [showDealOptions, setShowDealOptions] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number>(0);
  const [discountRate, setDiscountRate] = useState<number>(10);
  
  // Verify authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (!token || !user) {
      navigate('/login');
    }
  }, [navigate]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticker.trim()) return;

    setIsSearching(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/eligibility`,
        { ticker: ticker.toUpperCase() },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const eligibilityData = response.data;
      setResult(eligibilityData);
      
      if (eligibilityData.eligible) {
        // Show deal options after successful eligibility check
        setTimeout(() => {
          setShowDealOptions(true);
        }, 1000);
      }
    } catch (err: any) {
      console.error('Eligibility check error:', err);
      setError(err.response?.data?.message || 'Error checking eligibility. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleCreateFacility = async () => {
    if (!result || !selectedAmount || !discountRate) return;

    try {
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Create new questionnaire/selection
      const response = await axios.post(
        `${API_URL}/api/questionnaire`,
        {
          ticker: result.ticker,
          companyName: result.companyName,
          selectedAmount: selectedAmount,
          discountRate: discountRate,
          includeAdvance: selectedAmount >= 1000000, // Auto-include advance for amounts >= $1M
          advanceAmount: selectedAmount >= 1000000 ? selectedAmount * 0.01 : 0, // 1% advance
          vwapPercent: 80,
          rapidPercent: 20,
          eligibilityCheckId: result.data?.eligibilityId || null
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Update user data with new facility
        const updatedUser = {
          ...userData,
          facilityId: response.data.facilityId,
          ticker: result.ticker,
          companyName: result.companyName
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Navigate to pre-closing checklist for new facility
        navigate(`/pre-closing-checklist?facilityId=${response.data.facilityId}`);
      }
    } catch (err: any) {
      console.error('Error creating facility:', err);
      setError(err.response?.data?.message || 'Error creating facility. Please try again.');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const predefinedAmounts = [
    { value: 500000, label: '$500K' },
    { value: 1000000, label: '$1M' },
    { value: 2500000, label: '$2.5M' },
    { value: 5000000, label: '$5M' },
    { value: 10000000, label: '$10M' },
    { value: 25000000, label: '$25M' }
  ];

  const predefinedDiscounts = [5, 10, 15, 20];

  // If showing deal options
  if (showDealOptions && result) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <button
                  onClick={() => setShowDealOptions(false)}
                  className="mr-4 text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                </button>
                <h1 className="text-2xl font-bold text-gray-900">Configure New Facility</h1>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Company Info */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Ticker</p>
                <p className="font-semibold">{result.ticker}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Company</p>
                <p className="font-semibold">{result.companyName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Market Cap</p>
                <p className="font-semibold">{formatCurrency(result.marketCap)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Last Close</p>
                <p className="font-semibold">{formatCurrency(result.lastClose)}</p>
              </div>
            </div>
          </div>

          {/* Facility Amount */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Facility Amount</h2>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {predefinedAmounts.map((amount) => (
                <button
                  key={amount.value}
                  onClick={() => setSelectedAmount(amount.value)}
                  className={`py-3 px-4 rounded-lg font-medium transition-colors ${
                    selectedAmount === amount.value
                      ? 'bg-navy-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {amount.label}
                </button>
              ))}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Amount
              </label>
              <input
                type="number"
                value={selectedAmount}
                onChange={(e) => setSelectedAmount(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                placeholder="Enter custom amount"
                min="100000"
                max="50000000"
                step="100000"
              />
            </div>
          </div>

          {/* Discount Rate */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Discount Rate</h2>
            <div className="grid grid-cols-4 gap-3 mb-4">
              {predefinedDiscounts.map((rate) => (
                <button
                  key={rate}
                  onClick={() => setDiscountRate(rate)}
                  className={`py-3 px-4 rounded-lg font-medium transition-colors ${
                    discountRate === rate
                      ? 'bg-navy-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {rate}%
                </button>
              ))}
            </div>
            <div className="flex items-center space-x-4">
              <input
                type="range"
                min="1"
                max="25"
                value={discountRate}
                onChange={(e) => setDiscountRate(Number(e.target.value))}
                className="flex-1"
              />
              <span className="font-semibold text-lg w-12">{discountRate}%</span>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-r from-navy-600 to-navy-700 rounded-lg shadow-sm p-6 mb-6 text-white">
            <h2 className="text-lg font-semibold mb-4">Facility Summary</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Total Commitment:</span>
                <span className="font-semibold">{formatCurrency(selectedAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span>Discount Rate:</span>
                <span className="font-semibold">{discountRate}%</span>
              </div>
              {selectedAmount >= 1000000 && (
                <div className="flex justify-between">
                  <span>Advance Draw (1%):</span>
                  <span className="font-semibold">{formatCurrency(selectedAmount * 0.01)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-white/20">
                <span>Effective Purchase Price:</span>
                <span className="font-semibold">
                  {formatCurrency(result.lastClose * (1 - discountRate / 100))}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={() => setShowDealOptions(false)}
              className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleCreateFacility}
              disabled={!selectedAmount || !discountRate}
              className="flex-1 bg-orange-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Facility
            </button>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Create New Equity Facility</h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 hover:text-gray-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-8"
        >
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Check Company Eligibility
            </h2>
            <p className="text-gray-600">
              Enter your company's ticker symbol to check eligibility for a new equity line facility
            </p>
          </div>

          <form onSubmit={handleSearch} className="space-y-6">
            <div>
              <label htmlFor="ticker" className="block text-sm font-medium text-gray-700 mb-2">
                Stock Ticker Symbol
              </label>
              <TickerSearchInput
                value={ticker}
                onChange={setTicker}
                placeholder="Enter ticker symbol (e.g., AAPL)"
                className="w-full"
                disabled={isSearching}
              />
            </div>

            <button
              type="submit"
              disabled={isSearching || !ticker.trim()}
              className="w-full bg-navy-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-navy-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSearching ? 'Checking Eligibility...' : 'Check Eligibility'}
            </button>
          </form>

          {/* Error Display */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg"
            >
              <div className="flex items-start">
                <ExclamationCircleIcon className="h-5 w-5 text-red-400 mt-0.5" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="mt-1 text-sm text-red-700">{error}</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Result Display */}
          {result && !showDealOptions && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-6 p-6 rounded-lg border-2 ${
                result.eligible
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-start">
                {result.eligible ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mt-1" />
                ) : (
                  <ExclamationCircleIcon className="h-6 w-6 text-red-500 mt-1" />
                )}
                <div className="ml-4 flex-1">
                  <h3 className={`text-lg font-semibold ${
                    result.eligible ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {result.eligible ? 'Eligible for Equity Line' : 'Not Eligible'}
                  </h3>
                  {result.message && (
                    <p className={`mt-2 ${
                      result.eligible ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {result.message}
                    </p>
                  )}
                  {result.reasons && result.reasons.length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {result.reasons.map((reason, index) => (
                        <li key={index} className="text-sm text-red-600">
                          • {reason}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AuthenticatedNewFacility;